import { Parser } from 'json2csv';
import * as XLSX from 'xlsx';

/**
 * Export data to CSV format and trigger download
 * @param {Array<any>} data - The data to export
 * @param {string} filename - The filename for the downloaded file
 */
export const exportToCSV = (data: any[], filename: string): void => {
    try {
        // If data is empty, show an error
        if (!data || data.length === 0) {
            console.error('No data to export');
            return;
        }

        // Create CSV parser
        const parser = new Parser();
        const csv = parser.parse(data);

        // Create a download link
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        // Create download link and trigger click
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        console.error('Error exporting to CSV:', error);
    }
};

/**
 * Export data to Excel format and trigger download
 * @param {Array<any>} data - The data to export
 * @param {string} filename - The filename for the downloaded file
 * @param {string} sheetName - The name of the worksheet
 */
export const exportToExcel = (data: any[], filename: string, sheetName: string = 'Sheet1'): void => {
    try {
        // If data is empty, show an error
        if (!data || data.length === 0) {
            console.error('No data to export');
            return;
        }

        // Create a new workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.length > 31 ? sheetName.substring(0, 31) : sheetName);

        // Generate Excel file and trigger download
        XLSX.writeFile(workbook, `${filename}.xlsx`);
    } catch (error) {
        console.error('Error exporting to Excel:', error);
    }
};

/**
 * Prepare data for export based on chart type and component
 * @param {any[]} data - The raw data
 * @param {string} componentType - The type of component to prepare data for
 * @returns {any[]} - Prepared data for export
 */
export const prepareDataForExport = (data: any[], componentType: string): any[] => {
    switch (componentType) {
        case 'keyMetrics':
            return data.map(metric => ({
                Title: metric.title,
                Value: metric.value,
                Description: metric.description,
            }));
        case 'monthlyRevenue':
            return data.map(item => ({
                Month: item.monthFormatted,
                Net: item.net,
                Gross: item.gross,
            }));
        case 'subscriberStatus':
            return data.map(item => ({
                Status: item.name,
                Count: item.value,
                Percentage: `${((item.value / data.reduce((sum, i) => sum + i.value, 0)) * 100).toFixed(1)}%`,
            }));
        case 'performerRanking':
            return data.map(item => ({
                Date: item.dateFormatted,
                Ranking: `${item.performerTop}%`,
            }));
        case 'visitorTrends':
            return data.map(item => ({
                Date: item.dateFormatted,
                Visitors: item.visitors,
                Change: item.delta,
            }));
        case 'revenueBreakdown':
            const total = data.reduce((sum, item) => sum + item.value, 0);
            return data.map(item => ({
                Source: item.name,
                Amount: item.value,
                Percentage: `${((item.value / total) * 100).toFixed(1)}%`,
            }));
        case 'subscriberGrowth':
            return data.map(item => ({
                Date: item.dateFormatted,
                NewSubscribers: item.new,
                Renewals: item.renew,
                TotalActive: item.active,
            }));
        default:
            return data;
    }
};
